/**
 * THE DRIVER'S POSITION, WHILE THE TRIP SCREEN IS IN FRONT OF THEM.
 *
 * Reports `live_location` to `/portal/trip-event` on a timer for as long as
 * the journey is under way and this screen is visible. The API publishes each
 * one to the booking's partner; Booking.com takes `DRIVER_LIVE_LOCATION` as
 * often as it is sent, and is the only partner that has opted into driver
 * steps at all.
 *
 * IT IS ON FOR EVERY DRIVER, and there is no switch. That is the office's
 * decision, and the transparency it rests on is the line the trip screen shows
 * while it is running (portalFormat's LIVE_LOCATION_COPY): who sees the
 * position, and that it stops at the drop-off. Nothing here is conditional on
 * a stored preference — there is none to read.
 *
 * WHAT THIS IS NOT, AND CANNOT BE
 * -------------------------------
 * This is a web PWA, so there is no background tracking here and there is no
 * way to add it. A browser cannot reliably run a timer or read a position
 * while it is backgrounded, and on iOS it is not a matter of reliability: a
 * hidden tab is suspended, `watchPosition` stops, and a Home Screen app is
 * evicted outright. So reporting follows the SCREEN: it runs while the driver
 * has the trip open and the phone awake, and it stops the moment they switch
 * away, lock the phone or leave the screen.
 *
 * That is stated here rather than worked around on purpose. A service worker
 * cannot hold a geolocation watch; Background Sync and periodic sync do not
 * give a position and do not run on iOS at all; and a keep-alive that pings
 * without a fresh fix would report a stale place as a current one — which is
 * worse than reporting nothing, because a dispatcher cannot tell the two
 * apart. Continuous background tracking needs a native app, and nothing here
 * should pretend otherwise.
 *
 * WHEN IT REPORTS
 * ---------------
 * `isJourneyUnderWay` — the same `driver_status.state` the progress strip is
 * built from, so there is one idea of an active trip. The API enforces the
 * same window.
 *
 * HOW OFTEN, AND WHY SO SELDOM
 * ----------------------------
 * Every report is an SQS message on a FIFO queue, a Lambda invocation and an
 * HTTPS POST to Booking.com, plus a GPS fix on a phone the driver needs all
 * day. Booking.com publish no rate limit for this endpoint that we have been
 * able to find — their integration already treats a 429 as retryable, which
 * says there is one — so the interval is chosen CONSERVATIVELY and is a
 * constant, not a guess to be tuned by whoever is next in this file:
 *
 *   INTERVAL_MS (60 s) — about 25-40 reports on an airport transfer. A
 *     five-second feed would be 12x that, for a car that has moved 80 metres.
 *   MIN_MOVE_M (75 m) — below this the position has not really changed:
 *     consumer GPS noise alone is tens of metres, and a car in traffic or
 *     parked at the kerb would otherwise spend the whole wait sending the
 *     same point. This is what makes the pickup wait cost nothing.
 *
 * There is deliberately NO heartbeat: a stationary driver simply stops
 * reporting. Sending the same position repeatedly tells Booking.com nothing
 * it does not already have, and inventing a liveness requirement they have
 * not stated would be inventing volume. If their feed turns out to age a
 * position out, a heartbeat is one constant here.
 *
 * THE PERMISSION IS STILL THE REAL GATE, AND IT CANNOT BE NAGGED
 * -------------------------------------------------------------
 * Removing the in-app switch does not make reporting mandatory: the browser's
 * own location permission is not ours to grant, and a driver who denies it —
 * or revokes it later in their phone settings — sends nothing. What that must
 * NEVER become is a permission prompt raised every minute for a whole shift,
 * which is what a bare `setInterval` around `getCurrentPosition` would do to a
 * driver who keeps dismissing it. So the loop gives up:
 *
 *   - A DENIAL stops it at once (`PERMANENT_FIX_FAILURES`). The answer is no;
 *     asking again cannot change it, and every later call would be a prompt
 *     or a pointless wake of the GPS. Same for a browser with no geolocation.
 *   - ANYTHING ELSE EMPTY — no signal, a tunnel, a prompt left sitting there —
 *     is counted, and MAX_QUIET_ATTEMPTS of them in a row stops it too. This
 *     is the hard bound: however the browser behaves, this screen can raise at
 *     most MAX_QUIET_ATTEMPTS prompts in a journey and then goes quiet.
 *   - A SUCCESSFUL fix clears the count, so a tunnel costs nothing.
 *
 * Stopping lasts for the rest of the journey: it re-arms when the effect does
 * (the driver leaves this screen and comes back, or a new journey begins),
 * which is the natural place to retry a permission somebody may have changed
 * in their phone settings meanwhile.
 *
 * COORDINATES OR NOTHING
 * ----------------------
 * The most important rule in this file. Booking.com REQUIRE lat/lon for this
 * one event type and refuse it permanently without them — and a permanent
 * refusal is noted against the booking and the message deleted. So a fix that
 * comes back empty (permission denied, no signal, the prompt left sitting
 * there) is DROPPED HERE. It is never sent "without coordinates" the way a
 * status tap is, because a status tap is a fact that stands on its own and a
 * position with no position is nothing at all.
 */
import { useEffect, useRef } from 'react';
import { currentPositionResult, PERMANENT_FIX_FAILURES } from './currentPosition';
import { sendPortalTripEvent } from '../../Services/PortalApi';

/** How often a report is even considered. */
export const INTERVAL_MS = 60 * 1000;

/**
 * How far the driver must have moved since the LAST REPORT WE SENT for the
 * next one to be worth sending, in metres.
 */
export const MIN_MOVE_M = 75;

/**
 * How many attempts in a row may come back WITHOUT a usable fix before this
 * screen stops asking for the rest of the journey.
 *
 * This is the hard bound on nagging. A driver who dismisses the permission
 * prompt rather than answering it has not denied anything, so the browser may
 * put it up again on the next call — and a bare timer would therefore raise it
 * once a minute until they leave the trip screen. Whatever any browser does,
 * this screen asks at most this many times.
 *
 * Three rather than one because a tunnel, a multi-storey car park and the
 * first fix after a cold start all legitimately give nothing, and a successful
 * fix clears the count — so a journey only goes quiet after three unbroken
 * minutes of a phone that will not say where it is.
 */
export const MAX_QUIET_ATTEMPTS = 3;

const EARTH_RADIUS_M = 6371000;
const toRadians = (degrees) => (degrees * Math.PI) / 180;

/**
 * Metres between two fixes — the haversine distance, because a degree of
 * longitude is 75 km at the equator and 46 km in Budapest and a flat
 * subtraction would let a driver drift east for free.
 *
 * Returns Infinity when either point is missing, so "we have nothing to
 * compare against" always reads as "far enough to send".
 */
export const metresBetween = (a, b) => {
  if (!a || !b) return Infinity;

  const lat1 = Number(a.lat);
  const lon1 = Number(a.lon);
  const lat2 = Number(b.lat);
  const lon2 = Number(b.lon);

  if (![lat1, lon1, lat2, lon2].every(Number.isFinite)) return Infinity;

  const dLat = toRadians(lat2 - lat1);
  const dLon = toRadians(lon2 - lon1);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) * Math.sin(dLon / 2) ** 2;

  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * @param {object} options
 * @param {string} options.bookingId  which trip to report against.
 * @param {boolean} options.active    the journey is under way (and so this
 *                                    screen reports). False stops everything
 *                                    at once.
 */
export default function useLiveLocation({ bookingId, active }) {
  // The last position we actually SENT — the one the distance gate measures
  // against. A fix we dropped (too close, no coordinates) must not move it,
  // or a driver creeping forward 70 metres at a time would never report.
  const lastSent = useRef(null);
  // One report on the wire at a time. A fix can take six seconds (the
  // permission guard in currentPosition) and the POST its own; without this
  // a slow network would stack reports up behind each other.
  const inFlight = useRef(false);

  useEffect(() => {
    if (!active || !bookingId) {
      // Leaving the window — dropped off, or the office closed the trip —
      // forgets where they were. The next journey starts by sending its first
      // fix rather than comparing it to a position from another trip.
      lastSent.current = null;
      return undefined;
    }

    let stopped = false;
    let timer = null;
    // Attempts in a row that produced no usable fix. Reset by a good one.
    let quiet = 0;

    /**
     * Give up for the rest of this journey — the whole point of which is
     * that nothing asks the phone again. Clearing the timer rather than
     * only setting the flag is what makes that true of the GPS and the
     * permission prompt, not just of the POST.
     */
    const giveUp = () => {
      stopped = true;
      if (timer) clearInterval(timer);
      timer = null;
    };

    const report = async () => {
      // A hidden tab is the ordinary case on a phone, not an edge: the
      // driver locks it, takes a call, opens the map. The interval may
      // still fire (browsers throttle rather than stop it), and asking a
      // suspended page for a GPS fix is both pointless and a battery cost.
      if (stopped || inFlight.current) return;
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

      inFlight.current = true;
      try {
        const { point: fix, reason } = await currentPositionResult();

        if (stopped) return;

        // THE DRIVER SAID NO — or this browser has no geolocation at all.
        // Either way the answer will not change by asking again, and asking
        // again is exactly how a permission prompt ends up on somebody's
        // screen once a minute for a whole shift. Stop, for this journey.
        if (PERMANENT_FIX_FAILURES.includes(reason)) {
          giveUp();
          return;
        }

        // NO COORDINATES, NO REPORT. Booking.com refuse this event type
        // without lat/lon PERMANENTLY, and a permanent refusal is recorded
        // against the trip and thrown away — so there is nothing to be
        // gained by sending one and a rejection on the booking's own record
        // to lose. A status tap still goes out without a fix; a position
        // with no position is nothing at all.
        if (!fix || !Number.isFinite(fix.lat) || !Number.isFinite(fix.lon)) {
          // No signal, or a prompt nobody has answered. Worth asking again —
          // but not for ever.
          quiet += 1;
          if (quiet >= MAX_QUIET_ATTEMPTS) giveUp();
          return;
        }

        // A phone that is answering again starts its allowance over.
        quiet = 0;

        // Has the driver actually gone anywhere? The first fix of a
        // journey always passes (nothing to compare with).
        if (metresBetween(lastSent.current, fix) < MIN_MOVE_M) return;

        await sendPortalTripEvent({
          booking_id: bookingId,
          event: 'live_location',
          lat: fix.lat,
          lon: fix.lon,
        });

        if (!stopped) lastSent.current = fix;
      } catch (e) {
        // Quiet, and nothing retried. A position is worth nothing in a
        // minute's time because there will be another one; a driver must
        // never see an error once a minute because a partner is down, and
        // the API refuses a report outside the window on purpose.
      } finally {
        inFlight.current = false;
      }
    };

    // Nothing is sent on mount. The first report comes a whole interval
    // after the journey starts, which also gives the driver's own
    // "Úton vagyok" a minute's head start on the partner's queue — their
    // API wants a booking's first event to be that step.
    timer = setInterval(report, INTERVAL_MS);

    return giveUp;
  }, [active, bookingId]);
}
